/* eslint-disable react/prop-types */
/**
 * ReadOnlyChildTable — the read-only rendering half of the "Whole deal" scope
 * toggle. Renders a leading friendly "parent" column followed by the
 * metadata-driven data columns for the entity, with the parent FK column
 * stripped (the friendly label column replaces it). No create/edit/delete —
 * editing stays on the per-master (Selected) mode of each tab.
 *
 * Extracted from the old DealChildrenTab's ChildSection so the Lease/Deeds and
 * Wells tabs can both roll their sub-children up across the whole deal.
 */
import { useMemo } from "react";
import { WigAntTable } from "@warwick-investment-group/wig-react-library";

import { metadataV2ToColumns } from "../../../utils/metadataV2Columns";
import { PARENT_KEY } from "./dealWideChildren";

// The parent FK column is replaced by the friendly parent-label column, so
// strip it from the metadata-driven columns to avoid showing raw ids twice.
const STRIP_BY_TABLE = {
  lease_deed_tracts: new Set(["lease_deed_id"]),
  lease_deed_recordings: new Set(["lease_deed_id"]),
  well_tracts: new Set(["well_id"]),
  well_tract_regulatory: new Set(["well_tract_id"]),
};

const ReadOnlyChildTable = ({
  parentHeader,
  rows = [],
  metadata = [],
  tableName,
  loading = false,
  emptyText,
}) => {
  const columns = useMemo(() => {
    const strip = STRIP_BY_TABLE[tableName] || new Set();
    const dataCols = metadataV2ToColumns(
      (metadata || []).filter((m) => !strip.has(m.field))
    );
    const parentCol = {
      title: parentHeader,
      dataIndex: PARENT_KEY,
      key: PARENT_KEY,
      fixed: "left",
      width: 220,
      // The metadata-driven columns come with their own sorters; this one
      // is built here, so it needs one too.
      sorter: (a, b) =>
        String(a?.[PARENT_KEY] ?? "").localeCompare(
          String(b?.[PARENT_KEY] ?? "")
        ),
    };
    return [parentCol, ...dataCols];
  }, [metadata, tableName, parentHeader]);

  return (
    <WigAntTable
      size="small"
      rowKey={(r) => `${tableName}-${r.id}`}
      dataSource={rows}
      columns={columns}
      loading={loading}
      showSearch={false}
      showExport={false}
      showActionButton={false}
      pagination={{ size: "small", hideOnSinglePage: true, pageSize: 20 }}
      scroll={{ x: "max-content", y: 320 }}
      locale={{ emptyText: emptyText || "No records" }}
    />
  );
};

export default ReadOnlyChildTable;
